
import Vue from 'vue';
import HeaderLogo from './HeaderLogo.vue';
import HeaderLogoAdditional from './HeaderLogoAdditional.vue';
import HeaderMenu from './HeaderMenu.vue';
import HeaderActions from './HeaderActions.vue';
import HeaderCustomerMenu from '@root/common/components/layout/header/HeaderCustomerMenu.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import { gridConfig } from '@core/config/grid.config';
import { Header, HeaderActions as IHeaderActions } from '@root/modules/navigation/types/navigation';
import { Data as CategoriesData } from '@root/modules/category/types/categories';
import MenuLightbox from '@root/common/components/base/MenuLightbox.vue';
import { faArrowLeft } from '@delfi.lt/fa-kit/icons/modules/classic/regular';

const HeaderSitemap = () => import(/* webpackChunkName: "header/HeaderSitemap" */ './HeaderSitemap.vue');
const HeaderSearch = () => import(/* webpackChunkName: "header/HeaderSearch" */ './HeaderSearch.vue');

interface Props {
  navigation: Header;
  hideHeaderMenu: boolean;
}

interface Data {
  isSticky: boolean;
}

interface Methods {
  animateHeader: () => void;
  updateActionEvent: (type: IHeaderActions['type'], value: IHeaderActions['value']) => void;
  controlBodyOverflow: (isSitemapOpen: boolean) => void;
  handleOverflow: () => void;
  refetchFrontpage: () => void;
}

interface Computed {
  siteLogo: {
    src: {
      desktop: string | null;
      mobile: string | null;
    };
    url: string | null;
    alt: string;
    logoIsAnimated: boolean;
    logoWidth: number | null;
    logoWidthMobile: number | null;
    animatedLogoWidth: number | null;
  };
  isSearchOpen: boolean;
  isSitemapOpen: boolean;
  isProfileInfoOpen: boolean;
  isFrontpage: boolean;
  categoryData: CategoriesData['parentCategory'] | null;
  activeCategoryName: string | null;
  isMobileView: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'Header',
  components: {
    HeaderLogo,
    HeaderLogoAdditional,
    HeaderMenu,
    HeaderActions,
    HeaderSearch,
    HeaderSitemap,
    HeaderCustomerMenu,
    PianoCustomerStateHandler,
    MenuLightbox,
  },
  props: {
    navigation: {
      type: Object,
      required: true,
    },
    hideHeaderMenu: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isSticky: false,
      faArrowLeft,
    };
  },
  computed: {
    siteLogo() {
      const header = this.$channelConfig('header');
      const name = this.$channelConfig('settings').name;
      let customCategoryHeaderLogo = null;

      if (this.$route && this.$route.params.category) {
        const categorySlug = this.$route.params.subcategory || this.$route.params.category;
        const customCategorySettings = this.$channelConfig('page').category?.component?.header;
        if (customCategorySettings) {
          if (Array.isArray(customCategorySettings)) {
            const categoryAdditionalData =
              customCategorySettings.find((item: { categories: string | string[] }) => item.categories.includes(categorySlug)) || null;
            customCategoryHeaderLogo = categoryAdditionalData?.logo || null;
          }
        }
      }

      return {
        src: {
          desktop: customCategoryHeaderLogo || header?.logo || null,
          mobile: customCategoryHeaderLogo || header?.logoMobile || null,
        },
        url: header.logoUrl,
        alt: name,
        logoIsAnimated: !!header.logoIsAnimated,
        logoWidth: header?.logoWidth || null,
        logoWidthMobile: header?.logoWidthMobile || null,
        animatedLogoWidth: header?.animatedLogoWidth || null,
      };
    },
    isSearchOpen() {
      return this.$store.getters['navigation/getHeaderAction']('search');
    },
    isSitemapOpen() {
      return this.$store.getters['navigation/getHeaderAction']('sitemap');
    },
    isProfileInfoOpen() {
      return this.$store.getters['navigation/getHeaderAction']('profileInfo');
    },
    isFrontpage() {
      return this.$route.name === 'frontpage';
    },
    categoryData() {
      const category = this.$store.getters['category/getCategoryData'];
      return category?.parentCategory?.id ? category?.parentCategory : category;
    },
    activeCategoryName() {
      const id = this.$channelConfig('settings').id;
      return this.navigation.bottom?.parentSlug?.toLowerCase() || this.categoryData?.name?.toLowerCase() || id || null;
    },
    isMobileView() {
      if (process.client) {
        const isIpad = !!(/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1);

        return window.screen.width <= gridConfig.breakpoints.lg || isIpad;
      } else {
        return this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
      }
    },
  },
  watch: {
    isSitemapOpen(value: boolean) {
      this.controlBodyOverflow(value);
    },
    $route() {
      this.updateActionEvent('sitemap', false);
      this.updateActionEvent('profileInfo', false);
    },
  },
  mounted() {
    // Disable header sticky in Internet Explorer
    if (this.$ua.browser() !== 'Internet Explorer') {
      this.animateHeader();
      window.addEventListener('scroll', this.animateHeader);
      window.addEventListener('resize', this.animateHeader);
    }
  },
  destroyed() {
    if (this.$ua.browser() !== 'Internet Explorer') {
      window.removeEventListener('scroll', this.animateHeader);
      window.removeEventListener('resize', this.animateHeader);
    }
  },
  methods: {
    animateHeader() {
      const $menuTop: Element | null = document.querySelector('.header-menu--top');
      const stickyThreshold = this.$channelConfig('settings').name === 'multimedija' ? 0 : 30;
      const menuTopHeight: number = $menuTop?.clientHeight || stickyThreshold;
      const header = this.$refs.header as Element;

      if (window.innerWidth > gridConfig.breakpoints.lg) {
        const scroll: number = window.pageYOffset;
        // Make header sticky after scrolling down $menuTop height
        this.isSticky = scroll >= menuTopHeight;
      } else {
        this.isSticky = true;
      }

      // Set delay of header height definition caused by css transition
      setTimeout(() => {
        document.documentElement.style.setProperty('--delfi-header-height', `${header.clientHeight}px`);
      }, 305);
    },
    updateActionEvent(type, value) {
      // Add no-scroll when opening search
      if (type === 'search') {
        if (value === true) {
          document.body.classList.add('no-scroll');
        }
      }

      // Remove no-scroll when closing all overlays
      if (type === 'all' && !value) {
        document.body.classList.remove('no-scroll');
      }

      if (type !== 'all') {
        this.$store.commit('navigation/setHeaderAction', { type, value });
        return;
      }

      this.$store.commit('navigation/setHeaderAction', { type: 'sitemap', value });
      this.$store.commit('navigation/setHeaderAction', { type: 'search', value });
    },
    handleOverflow() {
      // Hide body overflow if sitemap is open on smaller devices
      const changeOverflow = !(window.innerWidth > gridConfig.breakpoints.lg) && this.isSitemapOpen ? 'add' : 'remove';
      document.body.classList[changeOverflow]('overflow-hidden');
    },
    controlBodyOverflow(isSitemapOpen) {
      const changeEventListener = isSitemapOpen ? 'addEventListener' : 'removeEventListener';

      this.handleOverflow();
      window[changeEventListener]('resize', this.handleOverflow);
    },
    refetchFrontpage() {
      if (this.$route.name === 'frontpage') {
        this.$store.commit('frontpage/setRefetchState', true);
      } else {
        this.$router.push('/');
      }
    },
  },
});
